
import { setTimeout } from 'timers'
import {Component, Emit, Prop, Ref, Vue, Watch} from 'vue-property-decorator'
import { MobileModule } from '../../store/modules/mobile'

@Component({})
export default class VideoPlayerComponent extends Vue {
  // 播放器ID，用于区分同一个页面上的多个双画面播放器。具体使用时，会以此ID为前缀，加载主画面和浮动画面固定ID之前
  @Prop({
    type: String,
    default: '',
    required: true,
  })
  playerId

  // 画面数量。当画面数量大于1时，才会展示浮动画面
  @Prop({
    type: Number,
    default: 1,
    required: true,
  })
  pictureCount

  mainPictureId = 'main-picture'
  floatingPictureId = 'floating-picture'

  mouseX: number // 鼠标相对于浏览器左上角的x轴的坐标
  mouseY: number // 鼠标相对于浏览器左上角的y轴的坐标
  floatingPictureLeft: number // 浮动画面距离父容器区域定位的top值
  floatingPictureTop: number // 浮动画面距离父容器区域定位的left值
  floatingPictureLeftMaxValue: number // 浮动画面区域的left值得极大值
  floatingPictureTopMaxValue: number // 浮动画面区域top值得极大值
  fakeFullScreen = false //ios中是否全屏
  showUserGuide = false //是否展示用户手势指引
  isDown = false //是否按下鼠标

  volume = 50 // 音量，默认为50。静音状态下，就是设置了音量也不生效
  muted = false // 是否静音，默认为不静音
  fullscreen = false // 当前是否在全屏，默认为否

  @Ref('parentArea') readonly parentAreaRef
  @Ref('mainPicture') readonly mainPicture
  @Ref('floatingPictureContainer') readonly floatingPictureContainer
  @Ref('floatingPicture') readonly floatingPicture
  @Ref('mainPictureControls') readonly mainPictureControls

  created(): void {
    if (this.playerId) {
      this.mainPictureId = this.playerId + '-' + this.mainPictureId
      this.floatingPictureId = this.playerId + '-' + this.floatingPictureId
    }
  }

  mounted(): void {
    window.addEventListener('resize', this.resize)

    // 监听全屏状态更改事件，全屏状态更改后，更新全局属性fullscreen的值
    this.parentAreaRef.addEventListener('webkitfullscreenchange', (event) => {
      let elem = event.target
      let isFullscreen = document.fullscreenElement === elem
      this.fullscreen = isFullscreen
    })
    this.parentAreaRef.onfullscreenchange = (event) => {
      let elem = event.target
      let isFullscreen = document.fullscreenElement === elem
      this.fullscreen = isFullscreen
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.resize)
  }

  resize() {
    this.$nextTick(() => {this.calcFloatingPictureAreaMaxValue()})
  }

  get orientation() {
    return MobileModule.orientation
  }

  @Watch('orientation')
  orientationWatch() {
    if(this.orientation && this.fakeFullScreen) {
      this.showUserGuide = true
      setTimeout(() => this.showUserGuide = false, 2000)
    }
  }

  @Watch('$store.state.mobile.onlineUser')
  onlineUserWatch(nv) {
    if (this.isMobile && nv) {
      this.$nextTick(() => this.calcFloatingPictureAreaMaxValue())
    }
  }
  // ---------------------  画面控制逻辑，如音量控制、全屏效果等 begin  ----------------------------------------------------------------------------------

  @Watch('volume', { deep: true })
  volumeWatch(nv, ov) {
    if (nv && nv !== ov) {
      this.setPlayerVolume()
    }
  }

  /**
   * 音量图标点击事件
   * <p>
   *  音量图标点击事件，更改当前视频的静音状态
   * </p>
   * @param muted 是否静音，默认为true
   */
  volumeIconClick(muted = true) {
    this.muted = muted
    this.setPlayerMuted()
  }

  /**
   * 全屏图标点击事件
   * <p>
   *  更改主播放区域的全屏播放状态
   * </p>
   * @param fullscreen  是否全屏，默认为false
   */
  fullscreenIconClick(fullscreen = false) {
    if (navigator.userAgent.indexOf('iPhone') > -1) {
      if (!this.fullscreen) {
        window.ontouchstart = () => {}
        this.fakeFullScreen = true
        this.fullscreen = true
        if(MobileModule.orientation){
          this.showUserGuide = true
          setTimeout(() => this.showUserGuide = false, 2000)
        }
      } else {
        window.ontouchstart = null
        this.fakeFullScreen = false
        this.fullscreen = false
      }
      this.$nextTick(() => {
        this.calcFloatingPictureAreaMaxValue()
      })
    } else {
      if (fullscreen) {
        if(this.parentAreaRef.requestFullScreen) {
          this.parentAreaRef.requestFullScreen()
        } else if(this.parentAreaRef.mozRequestFullScreen) {
          this.parentAreaRef.mozRequestFullScreen()
        } else if(this.parentAreaRef.webkitRequestFullScreen) {
          this.parentAreaRef.webkitRequestFullScreen()
        }
      } else {
        document.exitFullscreen().then(
          () => {},
          (err) => {
            console.error(err)
          }
        )
      }
    }

  }

  /**
   * 播放器开始播放事件监听回调函数
   * <p>
   *  如果播放器的数量大于1，那么音频画面播放器默认应该是浮动画面，否则为主画面。
   *  当监听到播放器开始播放视频流时，去获取一下音视频画面播放器当前默认的音量和静音状态。是全局变量中
   *  的静音状态和音量与播放器的保持一致。
   * </p>
   */
  videoPlayerListener() {
    this.setPlayerVolume()
    this.setPlayerMuted()
  }

  // ---------------------  画面控制逻辑，如音量控制、全屏效果等 end  ----------------------------------------------------------------------------------

  //--------------------------------------- 浮动画面随着鼠标移动而移动的效果实现相关函数 begin  -------------------------------------------------------------
  // 浮动画面随鼠标移动方案一：记下鼠标点击时候的位置，然后鼠标移动时，计算鼠标当前的位置与当初点击时的位置的差， 计算出浮动画面此时应该偏倚多少
  // 不论哪种方案，浮动画面所在区域距离浏览器左上角的X轴和Y轴的坐标，都要有一个极致，即浮动画面所在区域内。
  // 极值的范围：
  //  浮动画面所在区域的左上角距离浏览器左上角的X轴的坐标值不能超过（所在父容器的宽度 - 浮动画面区域自身的宽度 ）
  //  浮动画面所在区域的左上角距离浏览器左上角的Y轴的坐标值不能超过（所在父容器的高度 - 浮动画面区域自身的高度 ）

  /**
   * 监控画面数量更改。
   * 当画面数量大于1时，去计算浮动画面浮动值的极限值。同时为音视频画面播放器增加播放事件监听
   * @param nv
   * @param ov
   */
  @Watch('pictureCount', { deep: true, immediate: true })
  pictureCountWatch(nv, ov) {
    if (nv && nv !== ov) {
      if (nv > 1) {
        this.$nextTick(() => {
          this.calcFloatingPictureAreaMaxValue()
          // 如果视频画面大于1时，带音频的播放器应该是浮动画面播放器。
          // 取消主画面的play监听
          this.mainPicture.removeEventListener('play', this.videoPlayerListener)
          // 新增浮动画面的play监听
          this.floatingPicture.addEventListener(
            'play',
            this.videoPlayerListener
          )
        })
      } else {
        this.$nextTick(() => {
          // 如果视频画面等于1时，就只有一个主画面
          // 如果存在浮动画面，取消浮动画面的play监听
          if (this.floatingPicture) {
            this.floatingPicture.removeEventListener(
              'play',
              this.videoPlayerListener
            )
          }
          // 新增主画面的play监听
          this.mainPicture.addEventListener('play', this.videoPlayerListener)
        })
      }
    }
  }

  /**
   * 计算浮动画面区域的left和top值的最大值
   * <p>
   *  当页面初始化或者是页面元素重新加载后触发
   * </p>
   */
  calcFloatingPictureAreaMaxValue() {
    // 如果当前不存在浮动画面，不去计算这个极限值
    if (!this.floatingPictureContainer) {
      return
    }
    // console.log('我开始重新计算浮动画面的极限值了啊')
    var parentWidth = this.parentAreaRef.clientWidth
    var parentHeight = this.parentAreaRef.clientHeight
    // console.log('父容器宽 ： 【{}】，父容器高：【{}】', parentWidth, parentHeight)
    var floatingPictureAreaWidth = this.floatingPictureContainer.clientWidth
    var floatingPictureAreaHeight = this.floatingPictureContainer.clientHeight
    // console.log('浮动画面本身宽度 ： 【{}】，高：【{}】', floatingPictureAreaWidth, floatingPictureAreaHeight)

    this.floatingPictureLeftMaxValue = parentWidth - floatingPictureAreaWidth
    this.floatingPictureTopMaxValue = parentHeight - floatingPictureAreaHeight

    // console.log('计算结果， 距左的最大值为 ： 【{}】，距上的最大值为：【{}】', this.floatingPictureLeftMaxValue, this.floatingPictureTopMaxValue)
    // 移动端最大浮动变化后，需校验距离顶部的百分比的取值最大只能是极限值
    if (this.isMobile) {
      let top = this.floatingPictureContainer.style.top
      if (top) {
        top = top.substring(0,top.lastIndexOf('%'))
        const maxtop = this.pixelToPrecent(this.floatingPictureTopMaxValue,this.parentAreaRef.clientHeight)
        if (top > maxtop) {
          this.floatingPictureContainer.style.top = maxtop + '%'
        }
      }
    }
  }

  /**
   * 浮动画面区域的鼠标点击事件
   *
   * <p>
   *  记录当前的鼠标距离浏览器左上角的X轴和Y轴的坐标值、浮动画面区域相对于父容器的left和top值，
   *  用于后续鼠标移动时计算移动的距离和当下应该在的位置。
   * </p>
   *
   * @param e
   */
  mouseDown(e) {
    // 获取浮动画面元素
    if (!this.floatingPictureContainer) {
      return
    }
    // 更改浮动画面区域的鼠标的样式
    this.floatingPictureContainer.style.cursor = 'move'
    // 记录当前浮动画面区域的 left 和 top 的值
    let left = this.floatingPictureContainer.offsetLeft
    //  Number(this.floatingPictureContainer.style.left ? this.floatingPictureContainer.style.left.replace('%', '') : 0)
    let top = this.floatingPictureContainer.offsetTop
    // Number(this.floatingPictureContainer.style.top ? this.floatingPictureContainer.style.top.replace('%', '') : 0)

    this.floatingPictureLeft = left
    this.floatingPictureTop = top

    // 记录当前鼠标距离浏览器左上角的x轴和Y轴的坐标值
    this.mouseX = e.clientX || e.changedTouches[0].clientX
    this.mouseY = e.clientY || e.changedTouches[0].clientY
    // console.log(`mouseX : [${this.mouseX}] , mouseY : [${this.mouseY}] . objx : [${this.floatingPictureX}] , objy : [${this.floatingPictureY}]`)

    // 隐藏控制栏
    this.mainPictureControls.style.opacity = '0'
    this.mainPictureControls.style.display = 'none'
    // 鼠标是否点击标记值更改为true
    this.isDown = true
  }

  /**
   * 浮动画面区域鼠标移动事件
   * <p>
   *  根据鼠标移动后的坐标值与点击时记录的坐标值，计算位移的距离。 根据位移的距离计算当前浮动画面应该在的位置
   * </p>
   * @param e
   */
  mouseMove(e) {
    // 移动端阻止滚动向上传递
    e.preventDefault()
    // 获取浮动画面元素
    if (!this.floatingPictureContainer) {
      return
    }
    // 获取当前鼠标距离浏览器左上角的X轴和Y轴的坐标值
    var _mouseX = e.clientX || e.changedTouches[0].clientX
    var _mouseY = e.clientY || e.changedTouches[0].clientY
    if (this.isDown) {
      // 根据当前鼠标与点击时的偏倚量，去计算浮动画面区域的最新的left和top的值
      let left = this.floatingPictureLeft + _mouseX - this.mouseX
      left =
        left < 0
          ? 0
          : left < this.floatingPictureLeftMaxValue
          ? left
          : this.floatingPictureLeftMaxValue
      // console.log(`floatingPictureLeftMaxValue : [${this.floatingPictureLeftMaxValue}] , floatingPictureLeft : [${this.floatingPictureLeft}] . mouseX : [${this.mouseX}] , left : [${left}]`)
      let top = this.floatingPictureTop + _mouseY - this.mouseY
      top =
        top < 0
          ? 0
          : top < this.floatingPictureTopMaxValue
          ? top
          : this.floatingPictureTopMaxValue
      // console.log(`floatingPictureTopMaxValue : [${this.floatingPictureTopMaxValue}] , floatingPictureTop : [${this.floatingPictureTop}] . mouseY : [${this.mouseY}] , top : [${top}]`)
      // 将浮动值换算成百分比，以便于画面播放区域全屏或者取消全屏后仍然可以保持不变
      left = this.pixelToPrecent(left, this.parentAreaRef.clientWidth)
      top = this.pixelToPrecent(top, this.parentAreaRef.clientHeight)
      // 设置浮动画面区域当前的left和top值
      this.floatingPictureContainer.style.left = left + '%'
      this.floatingPictureContainer.style.top = top + '%'

      // console.log(`left : [${left}] , top : [${top}] .`)
    }
  }
  /**
   * 浮动画面区域鼠标取消点击事件
   * <p>
   *  更改标志位的值，还原鼠标的样式。
   * </p>
   * @param e
   */
  mouseUp(e) {
    // 获取浮动画面元素
    if (!this.floatingPictureContainer) {
      return
    }
    if (this.isDown) {
      this.isDown = false
      this.floatingPictureContainer.style.cursor = ''
      // 显示控制栏
      this.mainPictureControls.style.opacity = ''
      this.mainPictureControls.style.display = ''
    }
  }

  /**
   * 百分比转像素
   * <p>
   *  浮动画面的定位单位是百分比（方便全屏和退出全屏和可以保持相对位置），但是鼠标移动时，
   *  计算新的定位过程中需要用到像素值去计算。
   *  此方法就是用于这个过程中，将浮动画面拿到的定位的百分比值转换为像素值
   * </p>
   * @param {number} precent 当前获取到的百分比的值
   * @param {number} containerPixelValue  父容器的具体宽高的像素值。计算left时为父容器的宽度，计算top时传入父容器的高度
   */
  percentToPixel(precent: number, containerPixelValue: number): number {
    let value = ((precent * containerPixelValue) / 100).toFixed(2)
    return Number(value)
  }

  /**
   * 像素值转百分比
   * <p>
   *  浮动画面随鼠标移动而移动时，会根据当前鼠标偏移量的像素单位得到当前浮动画面定位的像素单位值。
   *  但是为了保持全屏和退出全屏时，浮动画面相对父容器可以保持定位不变，所以浮动画面定位样式的具体值应该是百分比值。
   *  此方法就是用于转换过程中，将得到的具体定位的像素单位值转换为百分比单位值。
   * </p>
   * @param {number} pixel 具体的像素单位的值
   * @param {number} containerPixelValue 父容器的具体宽高的像素值。计算left时为父容器的宽度，计算top时传入父容器的高度
   */
  pixelToPrecent(pixel: number, containerPixelValue: number): number {
    let value = ((pixel / containerPixelValue) * 100).toFixed(2)
    return Number(value)
  }

  //----------------------------------------------   浮动画面随着鼠标移动而移动的效果实现相关函数 end   ------------------------------------------------------

  @Emit()
  setPlayerMuted() {
    return {
      playerId: this.playerId,
      muted: this.muted
    }
  }

  @Emit()
  setPlayerVolume() {
    return {
      playerId: this.playerId,
      volume: (this.volume / 100).toFixed(2)
    }
  }
}
